import { useRef, useState } from 'react';
import { useSubmit } from '../../hooks/useSubmit';
import { cancelOrder } from '../adminApi';
import { Field } from '../ui/Field';
import { AutoTextarea } from '../ui/AutoTextarea';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { Spinner } from '../../ui/Spinner';
import { useRequestId } from '../../hooks/useRequestId';
import { refundableCents } from '../../core/rules/revenue';
import { money } from '../../core/format/currency';
import type { AdminOrder } from '../../core/types/adminRequests';
type Props = {
  order: AdminOrder;
  onDone: (order: AdminOrder) => void;
  onFailed: () => void;
  onClose: () => void;
};

/**
 * Annuleren: alleen zolang hij niet betaald is, of betaald en nog niet ingepakt (dan wordt alles
 * terugbetaald). De voorraad gaat terug. Zelfde reden = zelfde poging (`requestId`), zodat een
 * herhaling na een verloren antwoord niet dubbel annuleert.
 */
export function CancelForm({
  order,
  onDone,
  onFailed,
  onClose
}: Props) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  // De betaalstatus hoort bij de poging: kwam de betaling net binnen (409 `payment_changed`), dan
  // is annuleren daarna ook terugbetalen en dus een nieuwe poging.
  const requestId = useRequestId(`${order.paymentStatus}|${trimmed}`);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(cancelOrder);
  const paid = order.paymentStatus === 'betaald';
  const refund = paid ? refundableCents(order) : 0;
  const send = async () => {
    const result = await submit(order.id, {
      requestId,
      reason: trimmed
    });
    if (result) return onDone(result);
    onFailed();
    // Na de render staat de melding er; zet de focus erop zodat een schermlezer hem hoort.
    requestAnimationFrame(() => errorRef.current?.focus());
  };
  return <div className="rounded-lg border border-line bg-shell p-4 sm:p-5" role="alertdialog" aria-label="Bestelling annuleren" data-id="element-3359" data-mp="13is62mup1rmr">
      <h3 className="text-[15px] font-medium text-ink" data-id="element-3360" data-mp="2yob33w14feoi">Bestelling annuleren</h3>
      <p className="mt-0.5 text-[13px] leading-relaxed text-muted" data-id="element-3361" data-mp="2ym715jms8pqv">
        {paid ? `De klant krijgt ${money(refund)} terug via Mollie en een mail. De producten gaan terug op voorraad. Dit kan niet ongedaan worden gemaakt.` : 'Er is nog niet betaald, dus er valt niets terug te betalen. De producten gaan terug op voorraad en de betaallink vervalt.'}
      </p>
      <div className="mt-4 space-y-3" data-id="element-3362" data-mp="2fr03gabkuj8j">
        <Field label="Reden" hint="Voor je eigen administratie." data-id="element-3363" data-mp="2ko0aang93q0p">
          <AutoTextarea autoFocus value={reason} onChange={e => setReason(e.target.value)} minRows={2} maxLength={300} className={inputCls} data-id="element-3364" data-mp="2kqcr2xa9vuc6" />
        </Field>
        {(error || fieldErrors.reason) && <p ref={errorRef} tabIndex={-1} role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink outline-none" data-id="element-3365" data-mp="2ukd6brz9qfym">
            {fieldErrors.reason ?? error}
          </p>}
        <div className="flex flex-wrap gap-2" data-id="element-3366" data-mp="9y1tgm3bh1cu">
          <button type="button" onClick={() => void send()} disabled={submitting || !trimmed} aria-busy={submitting} className={btnPrimary} data-id="element-3367" data-mp="1uej6r1m0wb7l">
            {submitting && <Spinner data-id="element-3368" data-mp="1phiyql5m4ydu" />}
            {submitting ? 'Bezig…' : paid ? `Annuleren en ${money(refund)} terugbetalen` : 'Bestelling annuleren'}
          </button>
          <button type="button" onClick={onClose} disabled={submitting} className={btnSecondary} data-id="element-3369" data-mp="1fok9d12al3t6">
            Niet annuleren
          </button>
        </div>
      </div>
    </div>;
}